import { useWatch } from 'react-hook-form';
import { dsClass } from '../../../common/constants/dsClasses.js';
import { SelectedPurposeOfUseOrContact } from '../../../common/enums.js';
import { purposeOfUseExampleMsg, purposeOfUseMsg, t, userMsg } from '../../../common/i18n/index.js';
import { TextInput } from '../../../common/react-hook-form/fields/index.js';
import { SelectGroupItemWithId } from '../../SelectGroup/SelectGroupItem.js';
import { CreateOrSelectContact } from './CreateOrSelectContact.js';
import { PurposeOfUseSelectGroup } from './PurposeOfUseSelectGroup.js';
import type { Contact } from '../../../generated/api/contact.js';
import type { ContactHeader } from '../../../generated/api/contactHeader.js';
import type { ReactNode } from 'react';

interface ContactOrPurposeOfUseSelectorProps {
  companyName?: string;
  contacts?: Contact[] | ContactHeader[];
  fieldPrefix?: string;
  isEmailMandatory?: boolean;
  isPhoneNumberMandatory?: boolean;
  onChangeContact?: (selectedContactId: string) => void;
  onContactOrPurposeChange?: () => void;
  /** Callback for explicit user creation - if provided, shows "Create new user" button */
  onCreateUser?: () => void;
  /** Whether user creation is in progress (shows loading state on button) */
  isCreatingUser?: boolean;
  showHeaderAndDescription?: boolean;
  useCreateContactModal?: boolean;
  label?: string;
  showPurposeOfUse?: boolean;
  contentAfterHeader?: ReactNode;
  /** When false, only the purpose of use is asked and no user information is collected. */
  showUserContact?: boolean;
  /** When false, neither the contact nor the purpose of use has to be filled in. */
  isUserOrPurposeMandatory?: boolean;
}

export const PurposeOfUseSelector = ({
  companyName,
  contacts,
  fieldPrefix = '',
  isEmailMandatory = true,
  isPhoneNumberMandatory = true,
  onChangeContact,
  onCreateUser,
  isCreatingUser,
  showHeaderAndDescription = true,
  useCreateContactModal = false,
  label,
  onContactOrPurposeChange,
  showPurposeOfUse = true,
  showUserContact = true,
  contentAfterHeader,
  isUserOrPurposeMandatory = true,
}: ContactOrPurposeOfUseSelectorProps) => {
  const selectedValue = useWatch({ name: `${fieldPrefix}selected` });
  const selectedFieldValue = selectedValue || SelectedPurposeOfUseOrContact.CONTACT;

  const purposeOfUseField = (
    <div className={`of-purpose-of-use__purpose_of_use_field ${dsClass.PADDING_TOP_4}`}>
      <TextInput
        name={`${fieldPrefix}purposeOfUse`}
        hint={t.TTZ4(purposeOfUseExampleMsg)}
        required={isUserOrPurposeMandatory}
        maxLength={100}
        placeholder={t.Y8KX(purposeOfUseMsg)}
        onChange={onContactOrPurposeChange}
      />
    </div>
  );

  if (!showUserContact) {
    return (
      <>
        <h4 className={`${dsClass.MARGIN_BOTTOM_3} ${dsClass.MARGIN_TOP_2}`}>{t.Y8KX(purposeOfUseMsg)}</h4>
        {purposeOfUseField}
      </>
    );
  }

  return (
    <>
      {showHeaderAndDescription && (
        <>
          <h4 className={`${dsClass.MARGIN_BOTTOM_3} ${dsClass.MARGIN_TOP_2}`}>
            {t.WPXQ('Select user or purpose of use')}
          </h4>
          <p className={dsClass.MARGIN_VERTICAL_3}>
            {t.PV9I(
              'By adding user information, you can manage products and services easier in the OmaElisa Users view. Select the purpose of use only when it involves, for example, a group of users.'
            )}
          </p>
          {contentAfterHeader}
        </>
      )}

      <PurposeOfUseSelectGroup
        className={`${dsClass.FONT_WEIGHT_600} ${dsClass.PADDING_0} ${dsClass.PADDING_BOTTOM_2} of-purpose-of-use__select-group`}
        selectedItem={selectedFieldValue}
        fieldPrefix={fieldPrefix}
        label={label}
        onChangeSelectedGroup={onContactOrPurposeChange}
      >
        <SelectGroupItemWithId title={t.LUZM(userMsg)} id={SelectedPurposeOfUseOrContact.CONTACT}>
          <CreateOrSelectContact
            companyName={companyName}
            contacts={contacts}
            fieldPrefix={fieldPrefix}
            isEmailMandatory={isEmailMandatory}
            isPhoneNumberMandatory={isPhoneNumberMandatory}
            onContactChange={onChangeContact}
            onCreateUser={onCreateUser}
            isCreatingUser={isCreatingUser}
            useCreateContactModal={useCreateContactModal}
            isContactMandatory={isUserOrPurposeMandatory}
          />
        </SelectGroupItemWithId>
        {showPurposeOfUse && (
          <SelectGroupItemWithId title={t.Y8KX(purposeOfUseMsg)} id={SelectedPurposeOfUseOrContact.PURPOSEOFUSE}>
            {purposeOfUseField}
          </SelectGroupItemWithId>
        )}
      </PurposeOfUseSelectGroup>
    </>
  );
};
